import React, { useState, useEffect, useMemo } from 'react';
import { Button, Form, Container, Row, Col, Alert } from 'react-bootstrap';
import { FaMinus, FaPlus } from 'react-icons/fa';
import { useUser } from '../../context/UserContext';
import { useTenant } from '../../context/TenantContext';
import { useMembership } from '../../context/MembershipContext';
import { createContact, updateContactContent } from '../../services/contactsService';
import { fetchUnitsForTenant } from '../../services/unitsService';
import {
  resolveContactListGroupId,
  resolveContactListGroups,
} from '../../services/contactListGroups';
import {
  MAX_CONTACT_EMAILS,
  createEmptyContactEntry,
  defaultContactEntryLabel,
  normalizeContactEmails,
} from '../../platform/contactEmails';
import { useToast } from '../../platform/toast/ToastProvider';
import {
  MAX_CONTACT_PHONES,
  normalizeContactPhones,
} from '../../platform/contactPhones';
import './ContactCreate.css';

function mapEntriesForForm(entries) {
  if (!entries?.length) {
    return [createEmptyContactEntry(0)];
  }

  return entries.map((entry, index) => ({
    label: entry.label || defaultContactEntryLabel(index),
    value: entry.value || '',
  }));
}

function ContactEntryRow({
  entry,
  index,
  valueType,
  valuePlaceholder,
  labelPlaceholder,
  canRemove,
  onLabelChange,
  onValueChange,
  onRemove,
}) {
  return (
    <div className="contact-create-entry-row">
      <Form.Control
        type="text"
        className="contact-create-entry-label"
        placeholder={labelPlaceholder}
        value={entry.label}
        onChange={(event) => onLabelChange(index, event.target.value)}
        aria-label="Oznaka"
      />
      <Form.Control
        type={valueType}
        className="contact-create-entry-value"
        placeholder={valuePlaceholder}
        value={entry.value}
        onChange={(event) => onValueChange(index, event.target.value)}
      />
      {canRemove && (
        <Button
          type="button"
          variant="outline-danger"
          size="sm"
          className="contact-create-multi-remove"
          aria-label="Odstrani vrstico"
          onClick={() => onRemove(index)}
        >
          <FaMinus aria-hidden="true" />
        </Button>
      )}
    </div>
  );
}

function ContactCreate({ onContactCreated, contactToEdit }) {
  const [name, setName] = useState('');
  const [emails, setEmails] = useState([createEmptyContactEntry(0)]);
  const [phones, setPhones] = useState([createEmptyContactEntry(0)]);
  const [listGroupId, setListGroupId] = useState('general');
  const [unit, setUnit] = useState('general');
  const [unitName, setUnitName] = useState('Splošno');
  const [units, setUnits] = useState([]);
  const [error, setError] = useState('');

  const { user } = useUser();
  const { tenantId, config } = useTenant();
  const { memberships } = useMembership();
  const toast = useToast();

  const contactGroups = useMemo(
    () => resolveContactListGroups(config),
    [config],
  );

  useEffect(() => {
    if (contactToEdit) {
      setName(contactToEdit.name);
      setEmails(mapEntriesForForm(contactToEdit.emails));
      setPhones(mapEntriesForForm(contactToEdit.phones));
      setUnit(contactToEdit.unit || 'general');
      setUnitName(contactToEdit.unitName || 'Splošno');
      setListGroupId(resolveContactListGroupId(contactToEdit));
      return;
    }

    setName('');
    setEmails([createEmptyContactEntry(0)]);
    setPhones([createEmptyContactEntry(0)]);
    setListGroupId('general');
    setUnit('general');
    setUnitName('Splošno');
  }, [contactToEdit]);

  useEffect(() => {
    if (!user || !tenantId) return;

    const loadUnits = async () => {
      const unitsList = await fetchUnitsForTenant(tenantId, { user, memberships });
      setUnits(unitsList);
    };

    loadUnits();
  }, [user, tenantId, memberships]);

  const handleGroupChange = (event) => {
    const nextGroupId = event.target.value;
    setListGroupId(nextGroupId);

    if (nextGroupId !== 'units') {
      setUnit('general');
      setUnitName('Splošno');
    }
  };

  const handleUnitChange = (event) => {
    const selectedUnitId = event.target.value;
    const selectedUnit = units.find((item) => item.id === selectedUnitId);
    setUnit(selectedUnitId);
    setUnitName(selectedUnit ? selectedUnit.unitName : 'Splošno');
  };

  const handleEmailLabelChange = (index, value) => {
    setEmails((current) => current.map((entry, entryIndex) => (
      entryIndex === index ? { ...entry, label: value } : entry
    )));
  };

  const handleEmailValueChange = (index, value) => {
    setEmails((current) => current.map((entry, entryIndex) => (
      entryIndex === index ? { ...entry, value } : entry
    )));
  };

  const handlePhoneLabelChange = (index, value) => {
    setPhones((current) => current.map((entry, entryIndex) => (
      entryIndex === index ? { ...entry, label: value } : entry
    )));
  };

  const handlePhoneValueChange = (index, value) => {
    setPhones((current) => current.map((entry, entryIndex) => (
      entryIndex === index ? { ...entry, value } : entry
    )));
  };

  const handleAddEmail = () => {
    if (emails.length >= MAX_CONTACT_EMAILS) {
      return;
    }
    setEmails((current) => [...current, createEmptyContactEntry(current.length)]);
  };

  const handleAddPhone = () => {
    if (phones.length >= MAX_CONTACT_PHONES) {
      return;
    }
    setPhones((current) => [...current, createEmptyContactEntry(current.length)]);
  };

  const handleRemoveEmail = (index) => {
    if (emails.length <= 1) {
      return;
    }
    setEmails((current) => current.filter((_, entryIndex) => entryIndex !== index));
  };

  const handleRemovePhone = (index) => {
    if (phones.length <= 1) {
      return;
    }
    setPhones((current) => current.filter((_, entryIndex) => entryIndex !== index));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!user) {
      setError('Za ustvarjanje kontakta morate biti prijavljeni.');
      return;
    }

    const normalizedEmails = normalizeContactEmails(emails);
    const normalizedPhones = normalizeContactPhones(phones);

    if (!name.trim() || !normalizedEmails.length || !normalizedPhones.length) {
      setError('Ime, vsaj en elektronski naslov in vsaj ena telefonska številka ne smejo biti prazni.');
      return;
    }
    if (listGroupId === 'units' && unit === 'general') {
      setError('Izberite organizacijsko enoto.');
      return;
    }

    const contactFields = {
      name: name.trim(),
      emails: normalizedEmails,
      phones: normalizedPhones,
      unit: listGroupId === 'units' ? unit : 'general',
      unitName: listGroupId === 'units' ? unitName : 'Splošno',
      listGroupId,
    };

    try {
      if (contactToEdit) {
        await updateContactContent(contactToEdit.id, contactFields);
        toast.success('Kontakt je bil posodobljen.');
      } else {
        await createContact(tenantId, {
          ...contactFields,
          uid: user.uid,
        });
        toast.success('Kontakt je bil dodan.');
      }

      setError('');
      onContactCreated();
    } catch (submitError) {
      setError('Ustvarjanje ali posodabljanje kontakta ni uspelo. Poskusite znova.');
      toast.error('Ustvarjanje ali posodabljanje kontakta ni uspelo. Poskusite znova.');
      console.error('Error creating or updating contact:', submitError);
    }
  };

  return (
    <Container className="contact-create-container">
      {error && <Alert variant="danger">{error}</Alert>}
      <Form onSubmit={handleSubmit}>
        <Row className="mb-3">
          <Col>
            <Form.Group>
              <Form.Label>Ime</Form.Label>
              <Form.Control
                type="text"
                placeholder="Vnesi ime kontakta"
                value={name}
                onChange={(event) => setName(event.target.value)}
              />
            </Form.Group>
          </Col>
        </Row>
        <Row className="mb-3">
          <Col>
            <Form.Group>
              <div className="contact-create-multi-header">
                <Form.Label className="mb-0">Elektronski naslov</Form.Label>
                {emails.length < MAX_CONTACT_EMAILS && (
                  <Button
                    type="button"
                    variant="outline-secondary"
                    size="sm"
                    onClick={handleAddEmail}
                  >
                    <FaPlus aria-hidden="true" />
                    Dodaj e-pošto
                  </Button>
                )}
              </div>
              <div className="contact-create-multi-list">
                {emails.map((entry, index) => (
                  <ContactEntryRow
                    key={`contact-email-${index}`}
                    entry={entry}
                    index={index}
                    valueType="email"
                    labelPlaceholder={defaultContactEntryLabel(index)}
                    valuePlaceholder="Elektronski naslov"
                    canRemove={emails.length > 1}
                    onLabelChange={handleEmailLabelChange}
                    onValueChange={handleEmailValueChange}
                    onRemove={handleRemoveEmail}
                  />
                ))}
              </div>
            </Form.Group>
          </Col>
        </Row>
        <Row className="mb-3">
          <Col>
            <Form.Group>
              <div className="contact-create-multi-header">
                <Form.Label className="mb-0">Telefon</Form.Label>
                {phones.length < MAX_CONTACT_PHONES && (
                  <Button
                    type="button"
                    variant="outline-secondary"
                    size="sm"
                    onClick={handleAddPhone}
                  >
                    <FaPlus aria-hidden="true" />
                    Dodaj telefon
                  </Button>
                )}
              </div>
              <div className="contact-create-multi-list">
                {phones.map((entry, index) => (
                  <ContactEntryRow
                    key={`contact-phone-${index}`}
                    entry={entry}
                    index={index}
                    valueType="tel"
                    labelPlaceholder={defaultContactEntryLabel(index)}
                    valuePlaceholder="Telefonska številka"
                    canRemove={phones.length > 1}
                    onLabelChange={handlePhoneLabelChange}
                    onValueChange={handlePhoneValueChange}
                    onRemove={handleRemovePhone}
                  />
                ))}
              </div>
            </Form.Group>
          </Col>
        </Row>
        <Row className="mb-3">
          <Col>
            <Form.Group>
              <Form.Label>Skupina</Form.Label>
              <Form.Control as="select" value={listGroupId} onChange={handleGroupChange}>
                {contactGroups.map((group) => (
                  <option key={group.id} value={group.id}>
                    {group.label}
                  </option>
                ))}
              </Form.Control>
            </Form.Group>
          </Col>
        </Row>
        {listGroupId === 'units' && (
          <Row className="mb-3">
            <Col>
              <Form.Group>
                <Form.Label>Organizacijska enota</Form.Label>
                <Form.Control as="select" value={unit} onChange={handleUnitChange}>
                  <option value="general" disabled>
                    Izberite enoto
                  </option>
                  {units.map((unitItem) => (
                    <option key={unitItem.id} value={unitItem.id}>
                      {unitItem.unitName}
                    </option>
                  ))}
                </Form.Control>
              </Form.Group>
            </Col>
          </Row>
        )}
        <Button variant="primary" type="submit">
          {contactToEdit ? 'Posodobi kontakt' : 'Dodaj kontakt'}
        </Button>
      </Form>
    </Container>
  );
}

export default ContactCreate;
